import { staticSettings } from "@montana/helpers/static-settings.helper";
import { createServiceFunction } from "../http";
import { IAddLinesGroupedByProduct } from "@montana/models/generated/cms.interface";
import { transformLDImageBackground, transformLDImageFrame, transformLDImageSize } from "@montana/helpers/london-dynamics.helper";
import { LDDefaultView, LDFrontView } from "@montana/constants/images";

export const fetchCart = createServiceFunction((cartGuid?: string) => {
    const url = `${staticSettings.apiUrl}/api/cart/${cartGuid && !staticSettings.b2b ? cartGuid : '00000000-0000-0000-0000-000000000000'}`;

    return f => f(url, {
        method: "GET",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
    });
});

export const deleteCart = createServiceFunction((cartGuid: string) => {
    const url = `${staticSettings.apiUrl}/api/episerver/cart/${cartGuid}`;

    return f => f(url, {
        method: "DELETE",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
    });
});

/**
 * A group of products to the card.
 * @param cartGuid Cart GUID
 * @param productsList Product or products to add to the card
 * @param productGuid If a specific GUID is desired for the product group
 */
export const postLine = createServiceFunction((
    cartGuid: string,
    lines: IAddLinesGroupedByProduct[]
) => {

    const url = `${staticSettings.apiUrl}/api/cart/${cartGuid}/lines`;

    const modifiedLines = lines.map(l => {
        l.productsList = l.productsList?.map(pl => {
            if (!pl.imageUrl || !pl.imageUrl.includes('londondynamics')) {
                return pl;
            }

            const modulePictureWithCorrectSize = transformLDImageSize(pl.imageUrl, 230);
            const modulePictureWithCorrectBackground = transformLDImageBackground(modulePictureWithCorrectSize, 'FFFFFF');

            // BE requires two module pictures (POV: front and slightly rotated) for Montanas production - these needs to be well formed URLs as well!
            const modulePicture = encodeURI(transformLDImageFrame(modulePictureWithCorrectBackground, LDFrontView));
            const modulePicture2 = encodeURI(transformLDImageFrame(modulePictureWithCorrectBackground, LDDefaultView));

            return { ...pl, modulePicture, modulePicture2 };
        });

        return l;
    });

    return f => f(url, {
        method: "POST",
        body: JSON.stringify({ lines: modifiedLines }),
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
    });
})

export const putLine = createServiceFunction((cartGuid: string, productGroupId: number, quantity: number) => {
    const url = `${staticSettings.apiUrl}/api/cart/${cartGuid}/lines/${productGroupId}/quantity/${quantity}`;

    return f => f(url, {
        method: "PUT",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
    });
})

export const deleteLine = createServiceFunction((cartGuid: string, productGroupId: number) => {
    const url = `${staticSettings.apiUrl}/api/cart/${cartGuid}/lines/${productGroupId}`;

    return f => f(url, {
        method: "DELETE",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
    });
});

export const postVoucherCode = createServiceFunction((cartGuid: string, voucherCode: string) => {
    const url = `${staticSettings.apiUrl}/api/cart/${cartGuid}/voucher/${voucherCode}`;

    return f => f(url, {
        method: "POST",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
    });
});

export const removeVoucherCode = createServiceFunction((cartGuid: string, voucherCode: string) => {
    const url = `${staticSettings.apiUrl}/api/cart/${cartGuid}/voucher/${voucherCode}`;

    return f => f(url, {
        method: "DELETE",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
    });
});

export const putOrder = createServiceFunction((cartId: string, addressId?: string, ProductRef?: string) => {
    const url = `${staticSettings.apiUrl}/api/order/${cartId}/create`;

    return f => f(url, {
        method: "POST",
        body: JSON.stringify({ addressId, ProductRef }),
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
    });
});

export const getCityMatchingZipCode = createServiceFunction((marketId: string, zipCode: string) => {
    const url = `${staticSettings.apiUrl}/api/cart/city/${marketId}/${zipCode}`;
    return f => f(url, {
        method: "GET",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        }
    })
});
